// @greenlight-spec: 28-it-dashboard-ui.md#audit--audit-log-status--query
// @greenlight-spec: 11-observability.md#proxy-attribution-coverage
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import type { StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import type { AppAttributionCoverage, AttributionStatus } from '@greenlight/shared';

const WINDOW_DAYS = 7;

/**
 * Amber marks the one status a person can act on. `partial` and
 * `background_only` stay neutral: an app that does background work is
 * *supposed* to read 0%, and colouring that amber would train IT to ignore the
 * colour.
 */
const STATUS_META: Record<
  AttributionStatus,
  { variant: StatusBadgeVariant; label: string; detail: string }
> = {
  gap: {
    variant: 'degraded',
    label: 'No user named',
    detail: 'Served signed-in users but forwarded no actor token.',
  },
  partial: {
    variant: 'inactive',
    label: 'Partial',
    detail: 'Some calls name a user; the rest may be background work.',
  },
  background_only: {
    variant: 'inactive',
    label: 'Background only',
    detail: 'No signed-in user activity recorded — consistent with unattended work.',
  },
  attributed: {
    variant: 'active',
    label: 'Attributed',
    detail: 'Every proxy call names a user.',
  },
};

function CoverageRow({ app }: { readonly app: AppAttributionCoverage }) {
  const meta = STATUS_META[app.status];
  return (
    <li className="flex flex-col gap-1.5 py-3 md:flex-row md:items-center md:gap-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">{app.app_name}</p>
        <p className="mt-0.5 text-[12px] text-muted-foreground">{meta.detail}</p>
      </div>
      <p className="text-[12px] tabular-nums text-muted-foreground md:w-40 md:text-right">
        {app.user_attributed_calls} of {app.proxy_calls} calls · {app.coverage_percent}%
      </p>
      <StatusBadge variant={meta.variant} className="self-start md:self-auto">
        {meta.label}
      </StatusBadge>
    </li>
  );
}

/**
 * Answers the question a design partner's IT asked when the trail showed page
 * after page of `System`: which of my apps *can't* name the person? An app with
 * no user-attributed proxy calls that served signed-in users anyway is the one
 * a builder agent can fix, by forwarding the actor token on its next touch.
 */
export function AttributionCoverage() {
  const { data, isLoading, isError } = api.audit.attribution.useQuery(
    ['audit-attribution', WINDOW_DAYS],
    { query: { window_days: WINDOW_DAYS } },
    { queryKey: ['audit-attribution', WINDOW_DAYS] },
  );

  if (isLoading) {
    return (
      <SectionCard title="User attribution">
        <Skeleton className="h-28 w-full rounded-gl-lg" data-testid="attribution-loading" />
      </SectionCard>
    );
  }

  if (isError || data?.status !== 200) {
    return (
      <SectionCard title="User attribution">
        <Alert variant="destructive" data-testid="attribution-error">
          <AlertTitle>Couldn't load attribution coverage</AlertTitle>
          <AlertDescription>
            Something went wrong reading per-app attribution. Retry in a moment.
          </AlertDescription>
        </Alert>
      </SectionCard>
    );
  }

  const { apps, total_apps_with_proxy_traffic: total, window_days: windowDays } = data.body;
  const gaps = apps.filter((app) => app.status === 'gap').length;

  return (
    <SectionCard title="User attribution">
      <p
        className="text-[13px] leading-relaxed text-muted-foreground"
        data-testid="attribution-coverage"
      >
        A proxy call recorded as <span className="font-medium text-foreground">System</span> means
        one of two things: no person was involved — background jobs, timers, scheduled work — or an
        app served a person and never forwarded the actor token. Background work is correct as-is;
        the second case is fixable.
      </p>

      {total === 0 ? (
        <p className="mt-4 text-[13px] text-muted-foreground" data-testid="attribution-empty">
          No proxied data calls in the last {windowDays} days, so there is nothing to attribute yet.
        </p>
      ) : (
        <>
          <p className="mt-3 text-[13px] text-foreground">
            {gaps === 0
              ? `No app is missing user attribution over the last ${windowDays} days.`
              : `${gaps} ${gaps === 1 ? 'app is' : 'apps are'} serving people without naming them, over the last ${windowDays} days.`}
          </p>
          <ul className="mt-1 divide-y divide-border">
            {apps.map((app) => (
              <CoverageRow key={app.app_id} app={app} />
            ))}
          </ul>
          {apps.length < total && (
            <p className="mt-3 text-[12px] text-[hsl(var(--text-tertiary))]">
              Showing {apps.length} of {total} apps with proxy traffic.
            </p>
          )}
        </>
      )}
    </SectionCard>
  );
}
